import { useEffect } from 'react'
import { configurePurchases, resetPurchases } from '../lib/purchases'
import { useAuthStore } from '../store/auth'
import { useSubscriptionStore } from '../store/subscription'

/**
 * Keeps the Caliu Pro entitlement fresh: refreshes from `GET /subscription` on
 * login, on window focus and on becoming visible (catches cross-platform
 * purchases; an iOS home-screen app resumes without a `focus`), and when the
 * network comes back, and resets on logout. Mirrors the Swift `SubscriptionManager` login/foreground refresh.
 */
export function useSubscriptionSync() {
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const email = useAuthStore((s) => s.user?.email ?? null)
  const name = useAuthStore((s) => s.user?.name ?? null)

  useEffect(() => {
    const { refresh, reset } = useSubscriptionStore.getState()
    if (!userId) {
      reset()
      resetPurchases()
      return
    }
    // Saved by another account: it says nothing about this one.
    const { ownerId } = useSubscriptionStore.getState()
    if (ownerId && ownerId !== userId) reset()
    // Tie RevenueCat Web Billing to the Caliu user (matches iOS App User ID) and
    // attach email/name as subscriber attributes. Re-runs on a name change.
    configurePurchases(userId, email, name)
    void refresh()
    const onFocus = () => void refresh()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    window.addEventListener('focus', onFocus)
    window.addEventListener('online', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('online', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [userId, email, name])
}
